import moment from 'moment';

import { formatMatchDate, titleCase } from '../../../../utils/formatting';
import { DEFAULT_PLAYER_NAME } from '../../../../utils/constants';

const FONT_FAMILY = '"Inter", Arial, sans-serif';

// Bare glyph: the v2 icon bakes in its own circle, which double-draws inside the
// circular lock buttons and shrinks the lock to half its intended size.
export const LOCK_ICON_SRC = '/img/v4/lock.svg';
export const VERIFIED_ICON_SRC = '/img/v4/icon-verified-checkmark-black.svg';
export const UTR_ICON_SRC = '/img/v4/icon-utr-performance.svg';

// Chart colors sourced from assets/myutr-uikit/_variables.scss
export const COLOR_CONSISTENT = '#008ded'; // $blue
export const COLOR_OVER = '#108b15'; // $v3-green-700
export const COLOR_UNDER = '#d60c00'; // $red
export const COLOR_ACCENT = '#00C0F7'; // $light-blue
export const COLOR_ACCENT_TOP = '#01dfcb'; // $teal
export const COLOR_GRAY = '#5d636b'; // $v3-cool-grey-800
export const COLOR_GRID = '#cfd7e2'; // $v3-cool-grey-400
export const COLOR_IMPACT = '#a7b2c1'; // $v3-cool-grey-600
export const COLOR_BAND = 'rgba(0, 192, 247, 0.12)';
export const COLOR_SELECTED = '#FB7800'; // $v3-orange
export const COLOR_CURRENT_UTR_LINE = '#000000'; // $black

export const DOT_COLORS = {
    blue: COLOR_CONSISTENT,
    green: COLOR_OVER,
    red: COLOR_UNDER,
};

export const IMPACT_DOT_DIAMETERS = [6, 11, 16];

const [SMALL_DOT_RADIUS, MID_DOT_RADIUS, LARGE_DOT_RADIUS] = IMPACT_DOT_DIAMETERS.map(diameter => diameter / 2);

export const matchWeights = {
    LOW: 'Low',
    MEDIUM: 'Medium',
    HIGH: 'High',
};

const MATCH_WEIGHT_RADII = new Map([
    [matchWeights.LOW.toLowerCase(), SMALL_DOT_RADIUS],
    [matchWeights.MEDIUM.toLowerCase(), MID_DOT_RADIUS],
    [matchWeights.HIGH.toLowerCase(), LARGE_DOT_RADIUS],
]);

const SCATTER_X_STEP_MONTHS = 3;
const SCATTER_X_TICK_STEP_MONTHS = 1;
const SCATTER_X_BASE_RANGE_MONTHS = 12;
const SCATTER_X_TICK_FONT_DESKTOP = 12;
const SCATTER_X_TICK_FONT_MOBILE = 10;
const SCATTER_MONTH_FORMAT = 'MMM';
const SCATTER_MONTH_YEAR_FORMAT = 'MMM [\']YY';
const SCATTER_YEAR_SUFFIX_FORMAT = '[\']YY';
const SCATTER_Y_STEP = 0.25;
const SCATTER_MIDLINE_LABEL_CLEARANCE = SCATTER_Y_STEP / 2;
const SCATTER_MIDLINE_FONT_DESKTOP = 12;
const SCATTER_MIDLINE_FONT_MOBILE = 10;
const SCATTER_GRID_DASH = [2, 2];
const LAST_RESULTS_COUNT = 3;

export const CHART_CANVAS_HEIGHT_PX = 360;

export const dotColor = (color) => {
    return DOT_COLORS[color] || COLOR_CONSISTENT;
};

const matchWeightKey = (matchWeight) => {
    return typeof matchWeight === 'string' ? matchWeight.trim().toLowerCase() : '';
};

export const dotRadius = (matchWeight) => {
    return MATCH_WEIGHT_RADII.get(matchWeightKey(matchWeight)) ?? SMALL_DOT_RADIUS;
};

export const hasSinglesGraphData = (data) => {
    if (!data) { return false; }
    if (data.hasData === false) { return false; }
    return (data.graphs?.singles?.scatterplot?.data?.length ?? 0) > 0;
};

export const resolveCurrentUtr = (singles, fallbackUtr = null) => {
    const min = singles?.minConsistencyBand;
    const max = singles?.maxConsistencyBand;
    if (typeof min === 'number' && typeof max === 'number') {
        return Math.round(((min + max) / 2) * 100) / 100;
    }
    return fallbackUtr;
};

export const formatConsistencyLabel = (singles) => {
    if (typeof singles?.volatility !== 'number') { return null; }
    const hasBand = typeof singles.minConsistencyBand === 'number' && typeof singles.maxConsistencyBand === 'number';
    return {
        value: singles.volatility.toFixed(2),
        range: hasBand ? `${singles.minConsistencyBand.toFixed(2)} - ${singles.maxConsistencyBand.toFixed(2)}` : null,
    };
};

export const formatRangeLabel = (singles) => {
    if (typeof singles?.minMatchRating !== 'number' || typeof singles?.maxMatchRating !== 'number') { return null; }
    return `${singles.minMatchRating.toFixed(2)} - ${singles.maxMatchRating.toFixed(2)}`;
};

export const formatPercentValue = (fraction) => {
    if (typeof fraction !== 'number') { return null; }
    const percent = Math.round(Math.abs(fraction) * 1000) / 10;
    return Number.isInteger(percent) ? String(percent) : percent.toFixed(1);
};

export const performanceDirections = {
    ABOVE: 'above',
    BELOW: 'below',
    AT: 'at',
};

export const formatPerformanceInsight = (matchPerformancePercent) => {
    if (typeof matchPerformancePercent !== 'number') { return null; }
    let direction = performanceDirections.AT;
    if (matchPerformancePercent > 0) {
        direction = performanceDirections.ABOVE;
    } else if (matchPerformancePercent < 0) {
        direction = performanceDirections.BELOW;
    }
    return {
        percentLabel: formatPercentValue(matchPerformancePercent),
        direction,
    };
};

export const formatImpactWeight = (matchWeight) => {
    const key = matchWeightKey(matchWeight);
    return MATCH_WEIGHT_RADII.has(key) ? key : null;
};

export const histogramLocked = (histogram) => {
    return (histogram?.matchesRequired ?? 0) > 0;
};

export const parseBucketRange = (label) => {
    const parts = String(label ?? '').split('-').map(part => Number.parseFloat(part));
    if (parts.length !== 2 || Number.isNaN(parts[0]) || Number.isNaN(parts[1])) { return null; }
    return { min: parts[0], max: parts[1] };
};

export const splitHistogramTickLabel = (label) => {
    const parts = String(label ?? '').split('-');
    if (parts.length !== 2) { return label; }
    return [parts[0].trim(), `-${parts[1].trim()}`];
};

export const sortHistogramBuckets = (buckets) => {
    return [...(buckets || [])].sort((a, b) => {
        const rangeA = parseBucketRange(a.x);
        const rangeB = parseBucketRange(b.x);
        return (rangeA?.min ?? 0) - (rangeB?.min ?? 0);
    });
};

export const histogramAnchorPosition = (buckets, currentUtr) => {
    if (typeof currentUtr !== 'number' || !buckets?.length) { return null; }
    const ranges = buckets.map(bucket => parseBucketRange(bucket.x));
    if (ranges.some(range => range == null)) { return null; }
    if (currentUtr <= ranges[0].min) { return { index: 0, fraction: 0 }; }
    const lastIndex = ranges.length - 1;
    if (currentUtr >= ranges[lastIndex].max) { return { index: lastIndex, fraction: 1 }; }
    const index = ranges.findIndex(range => currentUtr >= range.min && currentUtr <= range.max);
    if (index === -1) { return null; }
    const { min, max } = ranges[index];
    const width = max - min;
    return { index, fraction: width > 0 ? (currentUtr - min) / width : 0.5 };
};

const playerCardModel = (player) => {
    if (!player) { return null; }
    const lastName = player.lastName?.trim();
    const firstInitial = player.firstName?.trim()?.[0];
    const name = (firstInitial && lastName)
        ? `${firstInitial.toUpperCase()}. ${titleCase(lastName)}`
        : titleCase(player.displayName || lastName || player.firstName?.trim()) || DEFAULT_PLAYER_NAME;
    const rating = player.singlesUtrDisplay ||
        (typeof player.singlesUtr === 'number' ? player.singlesUtr.toFixed(2) : null);
    return {
        playerId: player.playerId,
        name,
        nationality: player.nationality,
        rating,
    };
};

const parseSets = (score, profileIsWinner) => {
    if (!score) { return []; }
    return Object.keys(score)
        .sort((a, b) => Number(a) - Number(b))
        .map((setKey) => {
            const set = score[setKey] || {};
            const meGames = profileIsWinner ? set.winner : set.loser;
            const opponentGames = profileIsWinner ? set.loser : set.winner;
            return {
                me: meGames,
                opponent: opponentGames,
                meTiebreak: profileIsWinner ? set.winnerTiebreak : set.tiebreak,
                opponentTiebreak: profileIsWinner ? set.tiebreak : set.winnerTiebreak,
            };
        });
};

export const buildMatchCardModel = (item, profilePlayerId) => {
    if (!item) { return null; }
    const detail = item.resultDetail;
    const base = {
        id: item.resultId,
        shortDateLabel: item.date,
        insight: formatPerformanceInsight(item.matchPerformancePercent),
        impactWeight: formatImpactWeight(item.matchWeight),
        match: null,
    };
    if (!detail) { return base; }
    const winner = playerCardModel(detail.winner1);
    const loser = playerCardModel(detail.loser1);
    const profileIsWinner = String(winner?.playerId) === String(profilePlayerId);
    const me = profileIsWinner ? winner : loser;
    const opponent = profileIsWinner ? loser : winner;
    return {
        ...base,
        shortDateLabel: detail.date ? formatMatchDate(detail.date, true) : item.date,
        match: {
            eventName: detail.eventName,
            me,
            opponent,
            profileIsWinner,
            sets: parseSets(detail.score, profileIsWinner),
        },
    };
};

export const mapResultCards = (results, profilePlayerId, count = LAST_RESULTS_COUNT) => {
    return (results || [])
        .slice(0, count)
        .map(item => buildMatchCardModel(item, profilePlayerId))
        .filter(Boolean);
};

const scatterBounds = (singles) => {
    const points = singles?.scatterplot?.data || [];
    const yCandidates = [
        singles?.minMatchRating,
        singles?.maxMatchRating,
        singles?.minConsistencyBand,
        singles?.maxConsistencyBand,
        ...points.map(point => point?.coordinates?.y),
    ].filter(value => typeof value === 'number');
    const xCandidates = points
        .map(point => point?.coordinates?.x)
        .filter(value => typeof value === 'number');
    const yMinValue = yCandidates.length ? Math.min(...yCandidates) : 0;
    const yMaxValue = yCandidates.length ? Math.max(...yCandidates) : 1;
    const xMaxValue = xCandidates.length ? Math.max(...xCandidates) : SCATTER_X_BASE_RANGE_MONTHS;
    return {
        yMin: Math.floor((yMinValue - 0.1) / SCATTER_Y_STEP) * SCATTER_Y_STEP,
        yMax: Math.ceil((yMaxValue + 0.1) / SCATTER_Y_STEP) * SCATTER_Y_STEP,
        xMax: Math.max(SCATTER_X_BASE_RANGE_MONTHS, Math.ceil(xMaxValue / SCATTER_X_STEP_MONTHS) * SCATTER_X_STEP_MONTHS),
    };
};

export const buildScatterData = (singles, selectedPointIndex = null) => {
    const points = singles?.scatterplot?.data || [];
    const pointColor = (point, pointIndex) => {
        return pointIndex === selectedPointIndex ? COLOR_SELECTED : dotColor(point?.color);
    };
    return {
        datasets: [{
            data: points.map(point => ({
                x: point?.coordinates?.x,
                y: point?.coordinates?.y,
                r: dotRadius(point?.matchWeight),
            })),
            backgroundColor: points.map(pointColor),
            borderColor: points.map(pointColor),
            borderWidth: 1,
            datalabels: { display: false },
        }],
    };
};

export const buildScatterCarouselCards = (singles, profilePlayerId) => {
    const points = singles?.scatterplot?.data || [];
    return points
        .map((point, pointIndex) => ({ point, pointIndex }))
        // x = months ago, so descending x reads oldest → newest, matching the
        // reversed chart's left-to-right order.
        .sort((a, b) => (b.point?.coordinates?.x ?? 0) - (a.point?.coordinates?.x ?? 0))
        .map(({ point, pointIndex }) => ({
            pointIndex,
            card: buildMatchCardModel(point, profilePlayerId),
        }))
        .filter(entry => entry.card != null);
};

// X values are "months ago" (0 = now). labelInterval mirrors the axis stepSize
// chosen in buildScatterOptions (monthly vs quarterly); a label gets the year
// suffix when it crosses a calendar year relative to the previous visible
// label, so the rule holds at both label densities.
export const scatterMonthTickLabel = (monthsAgo, isMobileWidth = false, labelInterval = null) => {
    const interval = labelInterval ?? (isMobileWidth ? SCATTER_X_STEP_MONTHS : SCATTER_X_TICK_STEP_MONTHS);
    if (monthsAgo % interval !== 0) { return ''; }
    const tickMonth = moment().subtract(monthsAgo, 'months');
    const previousVisibleMonth = moment().subtract(monthsAgo + interval, 'months');
    if (tickMonth.year() === previousVisibleMonth.year()) {
        return tickMonth.format(SCATTER_MONTH_FORMAT);
    }
    if (isMobileWidth) {
        return [tickMonth.format(SCATTER_MONTH_FORMAT), tickMonth.format(SCATTER_YEAR_SUFFIX_FORMAT)];
    }
    return tickMonth.format(SCATTER_MONTH_YEAR_FORMAT);
};

export const buildScatterOptions = (singles, currentUtr, onTooltip, isMobileWidth = false) => {
    const { yMin, yMax, xMax } = scatterBounds(singles);
    // Monthly ticks fit the standard 12-month desktop view; on mobile widths
    // and on spans beyond a year they overlap, so thin to quarterly.
    const xTickStep = (isMobileWidth || xMax > SCATTER_X_BASE_RANGE_MONTHS)
        ? SCATTER_X_STEP_MONTHS
        : SCATTER_X_TICK_STEP_MONTHS;
    const annotations = [];
    if (typeof singles?.minConsistencyBand === 'number' && typeof singles?.maxConsistencyBand === 'number') {
        annotations.push({
            type: 'box',
            xScaleID: 'x-axis-0',
            yScaleID: 'y-axis-0',
            xMin: 0,
            xMax,
            yMin: singles.minConsistencyBand,
            yMax: singles.maxConsistencyBand,
            backgroundColor: COLOR_BAND,
            borderColor: 'transparent',
            borderWidth: 0,
            drawTime: 'beforeDatasetsDraw',
        });
    }
    if (typeof currentUtr === 'number') {
        annotations.push({
            type: 'line',
            mode: 'horizontal',
            scaleID: 'y-axis-0',
            value: currentUtr,
            borderColor: COLOR_CURRENT_UTR_LINE,
            borderWidth: 1,
            borderDash: [4, 4],
            drawTime: 'beforeDatasetsDraw',
        });
    }
    return {
        maintainAspectRatio: false,
        responsive: true,
        legend: { display: false },
        hover: { mode: 'nearest', intersect: true },
        tooltips: { enabled: false, mode: 'nearest', intersect: true, custom: onTooltip },
        annotation: { annotations },
        scales: {
            xAxes: [{
                id: 'x-axis-0',
                type: 'linear',
                position: 'bottom',
                gridLines: { color: COLOR_GRID, drawBorder: true, borderDash: SCATTER_GRID_DASH, zeroLineColor: COLOR_GRID, zeroLineBorderDash: SCATTER_GRID_DASH },
                scaleLabel: {
                    display: true,
                    labelString: 'Time Period (Months)',
                    fontColor: COLOR_GRAY,
                    fontFamily: FONT_FAMILY,
                    fontSize: 12,
                    lineHeight: '16px',
                },
                ticks: {
                    min: 0,
                    max: xMax,
                    stepSize: xTickStep,
                    // X counts months into the past, so reverse makes time read
                    // left (oldest) to right (newest). autoSkip drops ticks by
                    // index regardless of label content, which would break the
                    // hand-managed month density — keep it off.
                    reverse: true,
                    autoSkip: false,
                    maxRotation: 0,
                    minRotation: 0,
                    fontColor: COLOR_GRAY,
                    fontFamily: FONT_FAMILY,
                    fontSize: isMobileWidth ? SCATTER_X_TICK_FONT_MOBILE : SCATTER_X_TICK_FONT_DESKTOP,
                    callback: value => scatterMonthTickLabel(value, isMobileWidth, xTickStep),
                },
            }],
            yAxes: [{
                id: 'y-axis-0',
                gridLines: { display: false, drawBorder: true, color: COLOR_GRID },
                scaleLabel: {
                    display: true,
                    labelString: 'Performance',
                    fontColor: COLOR_GRAY,
                    fontFamily: FONT_FAMILY,
                    fontSize: 14,
                    lineHeight: '20px',
                },
                ticks: {
                    min: yMin,
                    max: yMax,
                    stepSize: SCATTER_Y_STEP,
                    fontColor: COLOR_GRAY,
                    fontFamily: FONT_FAMILY,
                    fontSize: 14,
                    // '' clears the gutter around the midline value drawn by
                    // buildScatterMidlinePlugin.
                    callback: (value) => {
                        if (typeof currentUtr === 'number' && Math.abs(value - currentUtr) < SCATTER_MIDLINE_LABEL_CLEARANCE) {
                            return '';
                        }
                        return Number(value).toFixed(2);
                    },
                },
            }],
        },
    };
};

export const clampTooltipTop = ({ anchorY, tooltipHeight, boundsTop, boundsBottom }) => {
    const centeredTop = anchorY - (tooltipHeight / 2);
    const maxTop = boundsBottom - tooltipHeight;
    if (maxTop < boundsTop) { return boundsTop; }
    return Math.min(Math.max(centeredTop, boundsTop), maxTop);
};

export const TOOLTIP_ANCHOR_OFFSET_PX = 16;

// Grouped cards make the tooltip far wider than the old flip-at-60% heuristic
// assumed, so placement has to measure: sit beside the dot when there is room,
// flip to the other side when there is not, and centre-clamp when neither fits.
export const clampTooltipLeft = ({ anchorX, tooltipWidth, boundsWidth, offset = TOOLTIP_ANCHOR_OFFSET_PX }) => {
    if (tooltipWidth <= 0 || boundsWidth <= 0) { return anchorX + offset; }
    const maxLeft = boundsWidth - tooltipWidth;
    if (maxLeft < 0) { return 0; }
    const rightOfDot = anchorX + offset;
    if (rightOfDot <= maxLeft) { return rightOfDot; }
    const leftOfDot = anchorX - offset - tooltipWidth;
    if (leftOfDot >= 0) { return leftOfDot; }
    return Math.min(Math.max(anchorX - (tooltipWidth / 2), 0), maxLeft);
};

export const buildScatterMidlinePlugin = (currentUtr, isMobileWidth = false) => ({
    afterDraw(chart) {
        if (typeof currentUtr !== 'number') { return; }
        const yScale = chart.scales?.['y-axis-0'];
        if (!yScale) { return; }
        const gridLineOptions = yScale.options?.gridLines || {};
        const tickOptions = yScale.options?.ticks || {};
        // Mirrors chart.js 2.x's own y tick label layout so the midline value
        // right-aligns with the native tick labels in the axis gutter.
        const tickMarkLength = gridLineOptions.drawTicks === false ? 0 : (gridLineOptions.tickMarkLength || 0);
        const labelX = yScale.right - tickMarkLength - (tickOptions.padding || 0);
        const { ctx } = chart;
        ctx.save();
        ctx.fillStyle = COLOR_CURRENT_UTR_LINE;
        ctx.font = `600 ${isMobileWidth ? SCATTER_MIDLINE_FONT_MOBILE : SCATTER_MIDLINE_FONT_DESKTOP}px ${FONT_FAMILY}`;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText(currentUtr.toFixed(2), labelX, yScale.getPixelForValue(currentUtr));
        ctx.restore();
    },
});

const TOUCH_HIT_SLOP_PX = 8;

// Inverse of the overlay's rotate(-90deg) transform: chart.js hit-testing reads
// getBoundingClientRect() without accounting for CSS transforms, so taps on the
// rotated canvas must be mapped back into chart-space manually.
export const mapRotatedClientPoint = (clientX, clientY, canvasRect) => ({
    x: canvasRect.bottom - clientY,
    y: clientX - canvasRect.left,
});

export const findScatterPointIndexAt = (pointModels, position, hitSlop = TOUCH_HIT_SLOP_PX) => {
    let closestIndex = -1;
    let closestDistance = Infinity;
    (pointModels || []).forEach((model, modelIndex) => {
        if (!model || typeof model.x !== 'number' || typeof model.y !== 'number') { return; }
        const distance = Math.hypot(position.x - model.x, position.y - model.y);
        const threshold = (model.radius || 0) + hitSlop;
        if (distance <= threshold && distance < closestDistance) {
            closestDistance = distance;
            closestIndex = modelIndex;
        }
    });
    return closestIndex;
};

export const MAX_GROUPED_TOOLTIP_CARDS = 2;

// Dots whose circles intersect the hovered dot hide each other on the chart, so
// the tooltip has to speak for all of them. Deliberately non-transitive: the
// answer is always "what is underneath this dot", not "what cluster is this".
export const findOverlappingPointIndexes = (pointModels, index) => {
    const target = pointModels?.[index];
    if (!target || typeof target.x !== 'number' || typeof target.y !== 'number') { return []; }
    return (pointModels || []).reduce((indexes, model, modelIndex) => {
        if (modelIndex === index) {
            indexes.push(modelIndex);
            return indexes;
        }
        if (!model || typeof model.x !== 'number' || typeof model.y !== 'number') { return indexes; }
        const distance = Math.hypot(model.x - target.x, model.y - target.y);
        if (distance < (model.radius || 0) + (target.radius || 0)) {
            indexes.push(modelIndex);
        }
        return indexes;
    }, []);
};

export const buildOverlappingCardModels = (points, pointIndexes, profilePlayerId) => {
    return (pointIndexes || [])
        .map(pointIndex => (points || [])[pointIndex])
        .filter(Boolean)
        // x is months ago, so ascending x reads newest -> oldest.
        .sort((a, b) => (a?.coordinates?.x ?? 0) - (b?.coordinates?.x ?? 0))
        .map(point => buildMatchCardModel(point, profilePlayerId))
        .filter(Boolean);
};

export const buildHistogramData = (histogram) => {
    const buckets = sortHistogramBuckets(histogram?.data);
    return {
        labels: buckets.map(bucket => bucket.x),
        datasets: [{
            data: buckets.map(bucket => bucket.y),
            backgroundColor: COLOR_ACCENT,
            maxBarThickness: 110,
            datalabels: {
                anchor: 'end',
                align: 'top',
                color: COLOR_GRAY,
                font: { family: FONT_FAMILY, size: 14 },
                formatter: value => value,
            },
        }],
    };
};

export const buildHistogramOptions = (histogram, isMobileWidth = false) => {
    const counts = (histogram?.data || []).map(bucket => bucket.y);
    const maxCount = counts.length ? Math.max(...counts) : 0;
    // Every tick key is set unconditionally: react-chartjs-2 2.x merges options
    // into the live chart, so a key omitted on the desktop pass would keep its
    // mobile value when the viewport widens back out.
    const xTicks = {
        fontColor: COLOR_GRAY,
        fontFamily: FONT_FAMILY,
        fontSize: 14,
        maxRotation: 0,
        minRotation: 0,
        callback: isMobileWidth ? splitHistogramTickLabel : label => label,
    };
    return {
        maintainAspectRatio: false,
        responsive: true,
        legend: { display: false },
        // Top padding reserves room above the plot area for the anchor tick and
        // its current-UTR label drawn by buildHistogramAnchorPlugin.
        layout: { padding: { top: 32 } },
        tooltips: {
            backgroundColor: '#fff',
            displayColors: false,
            titleFontColor: COLOR_GRAY,
            titleFontFamily: FONT_FAMILY,
            bodyFontColor: '#2b3339',
            bodyFontFamily: FONT_FAMILY,
            callbacks: {
                title: items => `Rating ${items[0].label}`,
                label: item => `${item.value} matches`,
            },
        },
        scales: {
            xAxes: [{
                id: 'x-axis-0',
                gridLines: { display: false, drawBorder: true, color: COLOR_GRID },
                scaleLabel: {
                    display: true,
                    labelString: 'Rating Range',
                    fontColor: COLOR_GRAY,
                    fontFamily: FONT_FAMILY,
                    fontSize: 14,
                },
                ticks: xTicks,
            }],
            yAxes: [{
                id: 'y-axis-0',
                gridLines: { color: COLOR_GRID, drawBorder: true },
                scaleLabel: {
                    display: true,
                    labelString: 'No. of Matches',
                    fontColor: COLOR_GRAY,
                    fontFamily: FONT_FAMILY,
                    fontSize: 14,
                },
                ticks: { min: 0, suggestedMax: maxCount + 3, stepSize: 3, fontColor: COLOR_GRAY, fontFamily: FONT_FAMILY, fontSize: 14 },
            }],
        },
    };
};

// Tooltips are disabled because chart.js 2.x hit-testing ignores the CSS
// rotation applied by the landscape overlay, so taps would map to the wrong bar.
export const buildLandscapeHistogramOptions = (histogram) => {
    const options = buildHistogramOptions(histogram);
    options.tooltips = { enabled: false };
    return options;
};

const ANCHOR_TICK_HEIGHT = 12;
const ANCHOR_LABEL_GAP = 4;

export const buildHistogramAnchorPlugin = (histogram, currentUtr) => {
    const buckets = sortHistogramBuckets(histogram?.data);
    return {
        afterDraw(chart) {
            const position = histogramAnchorPosition(buckets, currentUtr);
            if (position == null) { return; }
            const xScale = chart.scales['x-axis-0'];
            const yScale = chart.scales['y-axis-0'];
            if (!xScale || !yScale || !buckets.length) { return; }
            const slotWidth = xScale.width / buckets.length;
            const slotX = xScale.left + slotWidth * (position.index + position.fraction);
            // Bars are narrower than their category slot (barPercentage /
            // maxBarThickness), so slot-based math can land in the gutters —
            // interpolate along the drawn bar's body instead.
            const barView = typeof chart.getDatasetMeta === 'function'
                ? chart.getDatasetMeta(0)?.data?.[position.index]?._view
                : null;
            const hasBarGeometry = typeof barView?.x === 'number' && typeof barView?.width === 'number';
            const x = hasBarGeometry
                ? (barView.x - (barView.width / 2)) + (position.fraction * barView.width)
                : slotX;
            const { ctx } = chart;
            ctx.save();
            ctx.strokeStyle = COLOR_GRAY;
            ctx.setLineDash([4, 4]);
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(x, yScale.top - ANCHOR_TICK_HEIGHT);
            ctx.lineTo(x, yScale.top);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.fillStyle = COLOR_GRAY;
            ctx.font = `600 14px ${FONT_FAMILY}`;
            ctx.textAlign = 'center';
            ctx.fillText(currentUtr.toFixed(2), x, yScale.top - ANCHOR_TICK_HEIGHT - ANCHOR_LABEL_GAP);
            ctx.restore();
        },
    };
};

const placeholderScatterPoint = (x, y, color, matchWeight) => ({
    color,
    date: null,
    resultId: null,
    coordinates: { x, y },
    matchPerformancePercent: null,
    matchWeight,
    resultDetail: null,
});

// The API omits the percent and weight on results a free user cannot see, so the
// teaser blurred behind the lock has nothing of its own to show.
export const LOCKED_PLACEHOLDER_INSIGHT = {
    percentLabel: '6.3',
    direction: performanceDirections.ABOVE,
};

export const LOCKED_PLACEHOLDER_IMPACT_WEIGHT = matchWeights.HIGH;

// A locked card without a handler renders a lock that silently does nothing, which
// plain PropTypes.func cannot catch.
export const lockHandlerPropType = (props, propName, componentName) => {
    if (props.locked && typeof props[propName] !== 'function') {
        return new Error(`\`${propName}\` is required in \`${componentName}\` when \`locked\` is true.`);
    }
    return null;
};

export const LOCKED_PLACEHOLDER_SINGLES = {
    volatility: 0.25,
    minConsistencyBand: 4.0,
    maxConsistencyBand: 4.5,
    minMatchRating: 3.5,
    maxMatchRating: 5.0,
    histogram: {
        data: [
            { x: '3.50 - 3.80', y: 3 },
            { x: '3.80 - 4.10', y: 9 },
            { x: '4.10 - 4.40', y: 12 },
            { x: '4.40 - 4.70', y: 10 },
            { x: '4.70 - 5.00', y: 6 },
        ],
        matchesRequired: 0,
        resultCount: 40,
    },
    scatterplot: {
        data: [
            placeholderScatterPoint(0.5, 4.2, 'blue', matchWeights.HIGH),
            placeholderScatterPoint(1.2, 4.8, 'green', matchWeights.LOW),
            placeholderScatterPoint(2.1, 4.3, 'blue', matchWeights.MEDIUM),
            placeholderScatterPoint(2.8, 3.7, 'red', matchWeights.LOW),
            placeholderScatterPoint(3.6, 4.4, 'blue', matchWeights.MEDIUM),
            placeholderScatterPoint(4.4, 4.9, 'green', matchWeights.MEDIUM),
            placeholderScatterPoint(5.3, 4.1, 'blue', matchWeights.LOW),
            placeholderScatterPoint(6.1, 3.8, 'red', matchWeights.LOW),
            placeholderScatterPoint(7.0, 4.35, 'blue', matchWeights.HIGH),
            placeholderScatterPoint(7.8, 4.7, 'green', matchWeights.LOW),
            placeholderScatterPoint(8.7, 4.25, 'blue', matchWeights.MEDIUM),
            placeholderScatterPoint(9.5, 4.15, 'blue', matchWeights.LOW),
            placeholderScatterPoint(10.4, 3.9, 'red', matchWeights.LOW),
            placeholderScatterPoint(11.2, 4.45, 'blue', matchWeights.MEDIUM),
        ],
    },
};
